import * as React from 'react';
import styled, {css} from 'styled-components';

import {getElectricTransitionAdvanced} from '../helpers/css';
import {TeaserLinkProps} from '../teaser-element/types';
import {focusableSelectors, includesSelector} from '../helpers';

interface StyledImageLinkProps {
    readonly disabled?: boolean;
    readonly isFocused?: boolean;
    readonly videoScaling?: boolean;
}

const TRANSITION_DURATION = '2.2s';

const disabledStyle = css`
    pointer-events: none;
    cursor: not-allowed;
`;

// apply style only to img element so it doesn't get applied to disclaimers
const hoverStyle = css`
    outline: 0;
    img {
        transform: scale(1.03);
    }
`;

const videoHoverStyle = css`
    // video {
    //     NOTE: no scaling for video
    //     it makes the animation slightly pixelated and not so smooth as static image
    // }
`;

const hoverEffect = css`
    :hover,
    :focus {
        ${hoverStyle};
    }
`;

const StyledImageLink = styled.a<StyledImageLinkProps>`
    text-decoration: none;
    display: block;
    cursor: pointer;
    overflow: hidden;
    ${props => props.isFocused && hoverStyle};
    ${props => props.isFocused && props.videoScaling && videoHoverStyle};
    ${props => (props.disabled ? disabledStyle : hoverEffect)};
    img,
    video {
        transition: ${getElectricTransitionAdvanced({
            name: 'transform',
            duration: TRANSITION_DURATION
        })};
    }
`;

export const ImageLink = React.forwardRef<
    HTMLAnchorElement,
    TeaserLinkProps & {children?: React.ReactNode}
>((props, ref) => {
    const {
        href,
        disabled,
        onClick,
        title,
        target,
        rel,
        onMouseOut,
        onMouseOver,
        isFocused,
        videoScaling,
        children
    } = props;

    React.useEffect(() => {
        const imageLinkRef = (
            ref as React.MutableRefObject<HTMLAnchorElement | null>
        )?.current;
        if (!imageLinkRef) return;

        const hasFocusableChildren = includesSelector(
            imageLinkRef,
            focusableSelectors
        )
            ? 'false'
            : 'true';

        imageLinkRef.setAttribute('aria-hidden', hasFocusableChildren);
    }, [ref]);

    return (
        <StyledImageLink
            ref={ref}
            href={href}
            onClick={(!disabled && onClick) || undefined}
            target={target}
            rel={rel}
            title={title}
            disabled={disabled}
            onMouseOver={onMouseOver}
            onMouseOut={onMouseOut}
            isFocused={isFocused}
            tabIndex={-1}
            videoScaling={videoScaling}
        >
            {children}
        </StyledImageLink>
    );
});
